import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", {
        email: email.trim(),
        origin_url: window.location.origin,
      });
      setSent(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send reset email");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="p-8 lg:p-16 grid place-items-center bg-background order-2 lg:order-1">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground" data-testid="forgot-back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
          </Link>
          <h1 className="font-semibold tracking-tight text-4xl mt-6">Forgot password.</h1>
          <p className="text-sm text-muted-foreground mt-2">Drop your email and we&apos;ll send a reset link. Good for 60 minutes.</p>

          {sent ? (
            <div className="mt-8 border border-emerald-300 bg-emerald-50 p-5" data-testid="forgot-sent">
              <CheckCircle2 className="w-7 h-7 text-emerald-700" strokeWidth={1.5} />
              <h3 className="font-semibold tracking-tight text-xl mt-3">Check your inbox.</h3>
              <p className="text-sm text-emerald-900/90 mt-2">
                If <strong className="font-mono-data text-xs">{email}</strong> has an account, a reset link is on its way.
                It expires in 60 minutes.
              </p>
              <Link to="/login" className="inline-block mt-5">
                <Button variant="outline" className="rounded-none h-10" data-testid="forgot-back-to-login">Back to sign in</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="forgot-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="font-mono-data text-xs uppercase tracking-widest">Email</Label>
                <Input id="email" required type="email" value={email} onChange={(e)=>setEmail(e.target.value)}
                  className="rounded-none h-11 border-foreground/30 focus-visible:ring-foreground"
                  data-testid="forgot-email-input" placeholder="you@company.com" />
              </div>
              <Button type="submit" disabled={busy} className="w-full rounded-none h-11 bg-foreground hover:bg-foreground/90" data-testid="forgot-submit">
                <Mail className="w-4 h-4 mr-2" /> {busy ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          )}
        </div>
      </div>
      <div className="hidden lg:flex items-center justify-center p-16 bg-foreground text-background order-1 lg:order-2">
        <div className="max-w-sm">
          <p className="font-mono-data text-xs uppercase tracking-widest text-background/60">// reset</p>
          <h2 className="font-semibold tracking-tight text-5xl mt-3">No drama.</h2>
          <p className="text-sm text-background/80 mt-4">
            One click in the email and you&apos;re back. The new password is yours alone — we never see it.
          </p>
        </div>
      </div>
    </div>
  );
}
